import { ReactNode, useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

import { Box, Flex, Portal } from '@chakra-ui/react';

export type MobileStickyBannerProps = {
  buttonSlot: ReactNode;
  isVisible: boolean;
};

export function MobileStickyBanner({ buttonSlot, isVisible }: MobileStickyBannerProps) {
  const bannerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = bannerRef.current;
    if (!node || !isVisible) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const pageOffset = entry.target.getBoundingClientRect().height;
        document.documentElement.style.setProperty('--mobile-sticky-banner-offset', `${pageOffset}px`);
      }
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty('--mobile-sticky-banner-offset');
    };
  }, [isVisible]);

  return (
    // Portal server-renders an in-flow placeholder <span> and removes it once it mounts. Callers
    // render this banner as a sibling of a card inside a flex column, so the placeholder claims a
    // flex gap that collapses on hydration and shifts the rest of the page up. The banner itself
    // is portaled to document.body, so keeping this wrapper out of layout costs nothing.
    <Box display='none'>
      <Portal>
        <AnimatePresence>
          {isVisible && (
            <Flex
              key='sticky-program-banner'
              ref={bannerRef}
              as={motion.div}
              bg='white'
              bgColor='supportBlue.20'
              border='solid 1px'
              borderBottomLeftRadius='0px'
              borderBottomRightRadius='0px'
              borderColor='supportBlue.70'
              borderTopLeftRadius='0px'
              borderTopRightRadius='0px'
              bottom='0'
              data-testid='sticky-program-banner'
              left='0px'
              p='12px 12px 18px'
              position='fixed'
              right='0px'
              shadow='lg'
              animate={{
                transition: { type: 'linear' },
                opacity: 1,
                y: 0,
              }}
              display={{
                base: 'flex',
                md: 'none',
              }}
              exit={{
                opacity: 0,
                y: 100,
              }}
              initial={{
                opacity: 0,
                y: 100,
              }}
            >
              {buttonSlot}
            </Flex>
          )}
        </AnimatePresence>
      </Portal>
    </Box>
  );
}
